// client/src/components/wetakeover/WalletConnect.jsx
import React, { useState } from 'react';
import { ethers } from 'ethers';

// This legacy wizard has no order/payment API integration. Keep checkout
// unavailable until a canonical flow is selected and wired end to end.
const CHECKOUT_AVAILABLE = false;

const WalletConnect = ({ totalAmount, onPaymentError }) => {
  const [step, setStep] = useState('invoice_question'); // invoice_question, invoice_form, wallet
  const [needsInvoice, setNeedsInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState({
    companyName: '',
    address: '',
    taxId: '',
    email: '',
  });
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [selectedToken, setSelectedToken] = useState('USDC');
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [txHash] = useState(null);
  const [balance, setBalance] = useState(null);

  const handleInvoiceChoice = (wantsInvoice) => {
    setNeedsInvoice(wantsInvoice);
    if (wantsInvoice) {
      setStep('invoice_form');
    } else {
      setStep('wallet');
    }
  };

  const handleInvoiceSubmit = () => {
    setStep('wallet');
  };

  const isInvoiceValid = () => {
    return (
      invoiceDetails.companyName &&
      invoiceDetails.address &&
      invoiceDetails.email
    );
  };

  const connectWallet = async () => {
    try {
      setPaymentStatus('connecting');

      if (!window.ethereum) {
        throw new Error('No wallet detected. Please install MetaMask or another Web3 wallet.');
      }

      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });

      const address = accounts[0];
      setWalletAddress(address);
      setWalletConnected(true);

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const balance = await provider.getBalance(address);
      setBalance(ethers.utils.formatEther(balance));

      setPaymentStatus('idle');
    } catch (error) {
      console.error('[WalletConnect] Connection error:', error);
      setPaymentStatus('error');
      onPaymentError(error);
    }
  };

  const disconnectWallet = () => {
    setWalletConnected(false);
    setWalletAddress(null);
    setBalance(null);
    setPaymentStatus('idle');
  };

  const executePayment = async () => {
    const error = new Error('Online checkout is not available yet. No transaction was requested.');
    setPaymentStatus('error');
    onPaymentError(error);
  };

  const getStatusText = () => {
    switch (paymentStatus) {
      case 'connecting':
        return 'Connecting Wallet...';
      case 'approving':
        return 'Approving Token...';
      case 'paying':
        return 'Processing Payment...';
      case 'confirming':
        return 'Confirming Transaction...';
      case 'success':
        return '✓ Payment Successful!';
      case 'error':
        return 'Payment Failed - Retry';
      default:
        return `Pay $${totalAmount.toLocaleString()} ${selectedToken}`;
    }
  };

  if (!CHECKOUT_AVAILABLE) {
    return (
      <div className="space-y-8 max-w-2xl mx-auto" role="status">
        <div className="text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            Checkout <span className="text-yellow-300">coming soon</span>
          </h2>
          <p className="text-white/70 text-lg">
            This preview is not connected to the TAKEOVER order or payment service yet.
          </p>
        </div>
        <div className="bg-yellow-300/10 rounded-[32px] p-8 border border-yellow-300/20 text-center">
          <p className="text-white mb-5">
            No wallet will be connected and no transaction will be requested from this page.
          </p>
          <a
            href="https://takeover.xyz/contact/"
            className="inline-flex bg-yellow-300 hover:bg-yellow-400 text-black font-bold px-8 py-4 rounded-[24px]"
          >
            Contact TAKEOVER
          </a>
        </div>
      </div>
    );
  }

  // Step 1: Invoice Question
  if (step === 'invoice_question') {
    return (
      <div className="space-y-8 max-w-2xl mx-auto">
        <div className="text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            Connect & <span className="text-yellow-300">Pay</span>
          </h2>
          <p className="text-white/70 text-lg">Secure payment via Web3 wallet</p>
        </div>

        {/* Amount Display */}
        <div className="bg-gradient-to-r from-yellow-300/10 to-yellow-300/5 rounded-[32px] p-8 border border-yellow-300/20 text-center">
          <div className="text-white/70 mb-2">Total Amount</div>
          <div className="text-5xl font-extrabold text-yellow-300 mb-4">
            ${totalAmount.toLocaleString()}
          </div>
          <div className="text-sm text-white/50">Payment in USDC or USDT</div>
        </div>

        {/* Invoice Question */}
        <div className="bg-white/5 rounded-[32px] p-8 border border-white/10">
          <h3 className="text-xl font-semibold text-white mb-4 text-center">
            Do you need an invoice?
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleInvoiceChoice(true)}
              className="p-6 rounded-[24px] border-2 border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all text-center"
            >
              <div className="text-3xl mb-2">📄</div>
              <div className="font-semibold">Yes, I need an invoice</div>
              <div className="text-xs text-white/50 mt-2">For business/tax purposes</div>
            </button>
            <button
              onClick={() => handleInvoiceChoice(false)}
              className="p-6 rounded-[24px] border-2 border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all text-center"
            >
              <div className="text-3xl mb-2">⚡</div>
              <div className="font-semibold">No, proceed to payment</div>
              <div className="text-xs text-white/50 mt-2">Skip invoice details</div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Invoice Form
  if (step === 'invoice_form') {
    return (
      <div className="space-y-8 max-w-2xl mx-auto">
        <div className="text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            Invoice <span className="text-yellow-300">Details</span>
          </h2>
          <p className="text-white/70 text-lg">Provide your billing information</p>
        </div>

        {/* Invoice Form */}
        <div className="bg-white/5 rounded-[32px] p-8 border border-white/10 space-y-4">
          <div>
            <label className="block text-white font-semibold mb-2">
              Company / Entity Name <span className="text-yellow-300">*</span>
            </label>
            <input
              type="text"
              value={invoiceDetails.companyName}
              onChange={(e) => setInvoiceDetails({ ...invoiceDetails, companyName: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-white/40 focus:border-yellow-300 focus:outline-none transition-all"
              placeholder="Your company name"
            />
          </div>

          <div>
            <label className="block text-white font-semibold mb-2">
              Billing Address <span className="text-yellow-300">*</span>
            </label>
            <textarea
              rows="3"
              value={invoiceDetails.address}
              onChange={(e) => setInvoiceDetails({ ...invoiceDetails, address: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-white/40 focus:border-yellow-300 focus:outline-none transition-all resize-none"
              placeholder="Full billing address"
            />
          </div>

          <div>
            <label className="block text-white font-semibold mb-2">Tax ID / VAT Number</label>
            <input
              type="text"
              value={invoiceDetails.taxId}
              onChange={(e) => setInvoiceDetails({ ...invoiceDetails, taxId: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-white/40 focus:border-yellow-300 focus:outline-none transition-all"
              placeholder="Optional"
            />
          </div>

          <div>
            <label className="block text-white font-semibold mb-2">
              Email for Invoice <span className="text-yellow-300">*</span>
            </label>
            <input
              type="email"
              value={invoiceDetails.email}
              onChange={(e) => setInvoiceDetails({ ...invoiceDetails, email: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-white/40 focus:border-yellow-300 focus:outline-none transition-all"
              placeholder="billing@company.com"
            />
          </div>

          <p className="text-xs text-white/50">* Required fields</p>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={() => setStep('invoice_question')}
            className="px-6 py-4 rounded-[24px] bg-white/5 hover:bg-white/10 border border-white/10 font-medium transition-all"
          >
            ← Back
          </button>
          <button
            onClick={handleInvoiceSubmit}
            disabled={!isInvoiceValid()}
            className="flex-1 bg-yellow-300 hover:bg-yellow-400 disabled:bg-yellow-300/30 disabled:cursor-not-allowed text-black font-bold py-4 rounded-[24px] transition-all"
          >
            Continue to Payment →
          </button>
        </div>
      </div>
    );
  }

  // Step 3: Wallet Connection & Payment
  return (
    <div className="space-y-8 max-w-2xl mx-auto">
      <div className="text-center">
        <h2 className="text-3xl md:text-5xl font-bold mb-4">
          Connect & <span className="text-yellow-300">Pay</span>
        </h2>
        <p className="text-white/70 text-lg">Secure payment via Web3 wallet</p>
      </div>

      {!walletConnected ? (
        <div className="space-y-6">
          {/* Amount Display */}
          <div className="bg-gradient-to-r from-yellow-300/10 to-yellow-300/5 rounded-[32px] p-8 border border-yellow-300/20 text-center">
            <div className="text-white/70 mb-2">Total Amount</div>
            <div className="text-5xl font-extrabold text-yellow-300 mb-4">
              ${totalAmount.toLocaleString()}
            </div>
            <div className="text-sm text-white/50">Payment in USDC or USDT</div>
          </div>

          {/* Invoice Summary (if provided) */}
          {needsInvoice && invoiceDetails.companyName && (
            <div className="bg-white/5 rounded-[24px] p-4 border border-white/10">
              <div className="flex items-center gap-2 text-sm text-white/70 mb-1">
                <span>📄</span>
                <span>Invoice will be sent to:</span>
              </div>
              <div className="text-white font-medium">{invoiceDetails.companyName}</div>
              <div className="text-white/50 text-sm">{invoiceDetails.email}</div>
            </div>
          )}

          {/* Single Connect Wallet Button */}
          <button
            onClick={connectWallet}
            disabled={paymentStatus === 'connecting'}
            className="w-full bg-yellow-300 hover:bg-yellow-400 active:bg-yellow-500 disabled:bg-yellow-300/50 text-black font-bold text-lg py-6 rounded-[32px] transition-all disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {paymentStatus === 'connecting' ? (
              'Connecting...'
            ) : (
              <>
                <svg className="w-6 h-6" viewBox="0 0 256 417" fill="currentColor">
                  <path d="M127.961 0l-2.795 9.5v275.668l2.795 2.79 127.962-75.638z" opacity="0.6"/>
                  <path d="M127.962 0L0 212.32l127.962 75.639V154.158z"/>
                  <path d="M127.961 312.187l-1.575 1.92v98.199l1.575 4.6L256 236.587z" opacity="0.6"/>
                  <path d="M127.962 416.905v-104.72L0 236.585z"/>
                </svg>
                Connect Wallet
              </>
            )}
          </button>

          {/* Back button if came from invoice */}
          {needsInvoice !== null && (
            <button
              onClick={() => setStep(needsInvoice ? 'invoice_form' : 'invoice_question')}
              className="w-full px-6 py-3 rounded-[24px] bg-white/5 hover:bg-white/10 border border-white/10 font-medium transition-all text-sm"
            >
              ← Back
            </button>
          )}

          {/* Security Notice */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-[24px] p-4">
            <div className="flex gap-3">
              <div className="text-blue-400 text-xl">🔒</div>
              <div className="text-sm text-white/70">
                Connect your Web3 wallet (MetaMask, WalletConnect, Coinbase, etc.) to proceed with payment.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Connected Wallet Display */}
          <div className="bg-white/5 rounded-[32px] p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <span className="text-white/70">Connected Wallet</span>
              <button
                onClick={disconnectWallet}
                className="text-sm text-red-400 hover:text-red-300"
              >
                Disconnect
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-300 flex items-center justify-center text-black font-bold">
                ✓
              </div>
              <div>
                <div className="font-mono text-sm">
                  {walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}
                </div>
                {balance && (
                  <div className="text-xs text-white/50">Balance: {parseFloat(balance).toFixed(4)} ETH</div>
                )}
              </div>
            </div>
          </div>

          {/* Token Selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-white/70">Select Payment Token</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSelectedToken('USDC')}
                className={`p-6 rounded-[24px] border-2 transition-all ${
                  selectedToken === 'USDC'
                    ? 'bg-yellow-300/10 border-yellow-300'
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="text-2xl mb-2">💵</div>
                <div className="font-bold">USDC</div>
                <div className="text-xs text-white/50 mt-1">USD Coin</div>
              </button>

              <button
                onClick={() => setSelectedToken('USDT')}
                className={`p-6 rounded-[24px] border-2 transition-all ${
                  selectedToken === 'USDT'
                    ? 'bg-yellow-300/10 border-yellow-300'
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="text-2xl mb-2">💰</div>
                <div className="font-bold">USDT</div>
                <div className="text-xs text-white/50 mt-1">Tether</div>
              </button>
            </div>
          </div>

          {/* Payment Button */}
          <button
            onClick={executePayment}
            disabled={paymentStatus !== 'idle' && paymentStatus !== 'error'}
            className="w-full bg-yellow-300 hover:bg-yellow-400 active:bg-yellow-500 disabled:bg-yellow-300/50 text-black font-bold text-lg py-6 rounded-[32px] transition-all disabled:cursor-not-allowed"
          >
            {getStatusText()}
          </button>

          {/* Transaction Status */}
          {txHash && (
            <div className="bg-white/5 rounded-[24px] p-4 border border-white/10">
              <div className="text-sm text-white/70 mb-2">Transaction Hash</div>
              <a
                href={`https://etherscan.io/tx/${txHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs text-yellow-300 hover:text-yellow-400 break-all"
              >
                {txHash}
              </a>
            </div>
          )}

          {/* Security Notice */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-[24px] p-4">
            <div className="flex gap-3">
              <div className="text-blue-400 text-xl">🔒</div>
              <div className="text-sm text-white/70">
                Your payment is secured by Ethereum blockchain. Never share your private keys.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletConnect;
